/**
 * SuperAdmin Analytics Page
 * Comprehensive analytics and reporting for SuperAdmin
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Card, Row, Col, Select, DatePicker, Button, Space, Statistic, Typography, Table, Tag } from 'antd';
import {
  BarChartOutlined,
  ReloadOutlined,
  UserOutlined,
  ApartmentOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import { Line, Column, Pie, Area } from '@ant-design/plots';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from '../components/superadmin/SuperAdminSidebar';
import SuperAdminHeader from '../components/superadmin/SuperAdminHeader';
import superAdminService from '../services/superAdminService';
import { useResponsive } from '../hooks/useResponsive';
import './SuperAdminAnalytics.css';
import type { ColumnsType } from 'antd/es/table';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface RecentRegistration {
  id: string;
  company: string;
  type: 'Macro' | 'Micro';
  status: 'Active' | 'Inactive';
  users: number;
  locations: number;
}

const SuperAdminAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState<string>('6months');
  const [, setDateRange] = useState<[any, any] | null>(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const months = timeRange === '1month' ? 1 : timeRange === '3months' ? 3 : timeRange === '6months' ? 6 : 12;
      const data = await superAdminService.getAnalytics(months);
      setAnalytics(data);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Auto-refresh analytics every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadAnalytics();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [loadAnalytics]);

  // Use real-time data from analytics
  const revenueData = analytics?.subscription_trends?.map((d: any) => ({
    month: d.month,
    revenue: d.active_subscriptions * 10000, // Estimate revenue
    subscriptions: d.active_subscriptions,
  })) || [];

  const companyGrowthData = analytics?.company_growth?.map((d: any) => ({
    month: d.month,
    new: d.new_companies,
    total: d.total_companies,
  })) || [];

  const userActivityData = analytics?.user_activity || [];

  const planDistributionData = analytics?.plan_distribution?.map((d: any) => ({
    type: d.plan_type,
    value: d.count,
    color: d.plan_type === 'Basic' ? '#1890ff' : d.plan_type === 'Premium' ? '#52c41a' : '#722ed1',
  })) || [];

  const companyTypeData = analytics?.license_type_distribution?.map((d: any) => ({
    type: d.license_type,
    value: d.count,
    color: d.license_type === 'Micro' ? '#13c2c2' : '#fa8c16',
  })) || [];

  const subscriptionStatusData = analytics?.subscription_status_distribution?.map((d: any) => ({
    status: d.status,
    value: d.count,
    color: d.status === 'Active' ? '#52c41a' : d.status === 'Expired' ? '#ff4d4f' : '#faad14',
  })) || [];

  const recentRegistrationsData: RecentRegistration[] = analytics?.recent_registrations?.map((r: any) => ({
    id: r.code,
    company: r.name,
    type: r.license_type === 'macro' ? 'Macro' : 'Micro',
    status: r.is_active ? 'Active' : 'Inactive',
    users: r.total_users,
    locations: r.total_locations,
  })) || [];

  // Revenue chart config
  const revenueChartConfig = {
    data: revenueData,
    xField: 'month',
    yField: 'revenue',
    smooth: true,
    color: '#1890ff',
    areaStyle: {
      fill: 'l(270) 0:#ffffff 0.5:#7ec2f3 1:#1890ff',
    },
    point: {
      size: 5,
      shape: 'circle',
    },
    tooltip: {
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';
        return `
          <div style="padding: 12px; background: white; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
            <div style="font-weight: 600; margin-bottom: 10px; color: #333; font-size: 14px;">${title}</div>
            ${items.map((item: any) => `
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 6px;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${item.color};"></span>
                <span style="color: #666; font-size: 13px;">Revenue:</span>
                <span style="color: ${item.color}; font-weight: 600; font-size: 13px;">Nu. ${Number(item.value).toLocaleString()}</span>
              </div>
            `).join('')}
          </div>
        `;
      },
    },
  };

  // Company growth chart config
  const companyGrowthChartConfig = {
    data: companyGrowthData.flatMap((d: { month: string; new: number; total: number }) => [
      { month: d.month, type: 'New Companies', value: d.new },
      { month: d.month, type: 'Total Companies', value: d.total },
    ]),
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    isStack: false,
    color: ['#52c41a', '#1890ff'],
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    legend: {
      position: 'top' as const,
    },
  };

  // User activity chart config
  const userActivityChartConfig = {
    data: userActivityData.flatMap((d: { month: string; active: number; new: number; total: number }) => [
      { month: d.month, type: 'Active Users', value: d.active },
      { month: d.month, type: 'New Users', value: d.new },
      { month: d.month, type: 'Total Users', value: d.total },
    ]),
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    smooth: true,
    color: ['#52c41a', '#fa8c16', '#1890ff'],
    point: {
      size: 4,
    },
    legend: {
      position: 'bottom' as const,
    },
  };

  // Plan distribution pie chart config
  const planDistributionConfig = {
    data: planDistributionData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: false, // Disable labels to avoid formatter issues
    legend: {
      position: 'bottom' as const,
      itemName: {
        formatter: (text: string) => {
          const item = planDistributionData.find((d: { type: string; value: number; color: string }) => d.type === text);
          if (!item) return text;
          const total = planDistributionData.reduce((sum: number, d: { type: string; value: number; color: string }) => sum + d.value, 0);
          const percentage = ((item.value / total) * 100).toFixed(1);
          return `${text}: ${percentage}%`;
        },
      },
    },
    color: planDistributionData.map((d: { type: string; value: number; color: string }) => d.color),
    interactions: [{ type: 'element-active' }],
  };

  // Company type pie chart config
  const companyTypeConfig = {
    data: companyTypeData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: false, // Disable labels to avoid formatter issues
    legend: {
      position: 'bottom' as const,
      itemName: {
        formatter: (text: string) => {
          const item = companyTypeData.find((d: { type: string; value: number; color: string }) => d.type === text);
          if (!item) return text;
          return `${text}: ${item.value}`;
        },
      },
    },
    color: companyTypeData.map((d: { type: string; value: number; color: string }) => d.color),
    interactions: [{ type: 'element-active' }],
  };

  // Subscription status pie chart config
  const subscriptionStatusConfig = {
    data: subscriptionStatusData,
    angleField: 'value',
    colorField: 'status',
    radius: 0.8,
    label: false, // Disable labels to avoid formatter issues
    legend: {
      position: 'bottom' as const,
      itemName: {
        formatter: (text: string) => {
          const item = subscriptionStatusData.find((d: { status: string; value: number; color: string }) => d.status === text);
          if (!item) return text;
          return `${text}: ${item.value}`;
        },
      },
    },
    color: subscriptionStatusData.map((d: { status: string; value: number; color: string }) => d.color),
    interactions: [{ type: 'element-active' }],
  };

  const handleRefresh = () => {
    setLoading(true);
    loadAnalytics().finally(() => {
      setLoading(false);
    });
  };

  // Recent Registrations table columns
  const recentRegistrationsColumns: ColumnsType<RecentRegistration> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'COMPANY',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        if (type === 'Macro') {
          return <Tag color="purple" style={{ borderRadius: '12px', padding: '4px 12px', fontWeight: 600, border: 'none', backgroundColor: '#e8dff8', color: '#7c4dff' }}>Macro</Tag>;
        }
        return <Tag color="blue" style={{ borderRadius: '12px', padding: '4px 12px', fontWeight: 600, border: 'none', backgroundColor: '#ddebfc', color: '#2196f3' }}>Micro</Tag>;
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (status === 'Active') {
          return <Tag color="green" style={{ borderRadius: '12px', padding: '4px 12px', fontWeight: 600, border: 'none', backgroundColor: '#e6f8ed', color: '#4caf50' }}>Active</Tag>;
        }
        return <Tag color="red" style={{ borderRadius: '12px', padding: '4px 12px', fontWeight: 600, border: 'none', backgroundColor: '#fbebeb', color: '#f44336' }}>Inactive</Tag>;
      },
    },
    {
      title: 'USERS',
      dataIndex: 'users',
      key: 'users',
      align: 'right',
      width: 100,
    },
    {
      title: 'LOCATIONS',
      dataIndex: 'locations',
      key: 'locations',
      align: 'right',
      width: 120,
    },
  ];

  const handleMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <Layout className={`superadmin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ minHeight: '100dvh', background: '#f0f2f5', display: 'flex', flexDirection: 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
      <SuperAdminHeader 
        onMenuClick={handleMenuClick}
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
      />
      <SuperAdminSidebar collapsed={sidebarCollapsed} onCollapse={setSidebarCollapsed} />
      <Content
        className="main-content-full superadmin-content"
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? 80 : 280),
          marginTop: isMobile ? 64 : 80,
          transition: 'all 0.2s',
          background: '#ffffff',
          padding: isMobile ? 12 : 24,
          width: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          maxWidth: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          minHeight: isMobile ? 'calc(100dvh - 64px)' : 'calc(100dvh - 80px)',
          overflow: 'auto',
        }}
      >
        <div className="analytics-page">
          {/* Page Header */}
          <div className="analytics-header">
            <div>
              <Title level={2} className="analytics-title">
                <BarChartOutlined /> Analytics & Reports
              </Title>
              <Text type="secondary">Comprehensive insights into your system performance</Text>
            </div>
            <Space>
              <Select
                value={timeRange}
                onChange={setTimeRange}
                style={{ width: 150 }}
                suffixIcon={null}
              >
                <Option value="1month">Last Month</Option>
                <Option value="3months">Last 3 Months</Option>
                <Option value="6months">Last 6 Months</Option>
                <Option value="1year">Last Year</Option>
                <Option value="custom">Custom Range</Option>
              </Select>
              {timeRange === 'custom' && (
                <RangePicker
                  onChange={(dates) => setDateRange(dates as [any, any])}
                  style={{ width: 250 }}
                  suffixIcon={null}
                />
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </div>

          {/* Key Metrics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="metric-card metric-card-blue" loading={analyticsLoading}>
                <Statistic
                  title="Total Companies"
                  value={analytics?.overview?.total_companies || 0}
                  prefix={<ApartmentOutlined />}
                  valueStyle={{ color: '#3B82F6' }}
                />
                <div className="metric-change positive">
                  <ArrowUpOutlined /> +{analytics?.overview?.companies_this_month || 0} this month
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="metric-card metric-card-green" loading={analyticsLoading}>
                <Statistic
                  title="Active Companies"
                  value={analytics?.overview?.active_companies || 0}
                  prefix={<ApartmentOutlined />}
                  valueStyle={{ color: '#22C55E' }}
                />
                <div className="metric-change positive">
                  <ArrowUpOutlined /> {analytics?.overview?.total_companies > 0 
                    ? ((analytics.overview.active_companies / analytics.overview.total_companies) * 100).toFixed(1)
                    : 0}% active
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="metric-card metric-card-purple" loading={analyticsLoading}>
                <Statistic
                  title="Total Users"
                  value={analytics?.overview?.total_users || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#A78BFA' }}
                />
                <div className="metric-change positive">
                  <ArrowUpOutlined /> +{analytics?.overview?.users_this_month || 0} this month
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="metric-card metric-card-orange" loading={analyticsLoading}>
                <Statistic
                  title="Total Locations"
                  value={analytics?.overview?.total_locations || 0}
                  prefix={<ApartmentOutlined />}
                  precision={0}
                  valueStyle={{ color: '#F97316' }}
                />
                <div className="metric-change positive">
                  <ArrowUpOutlined /> {analytics?.overview?.monthly_growth_rate >= 0 ? '+' : ''}{analytics?.overview?.monthly_growth_rate?.toFixed(1) || 0}% growth
                </div>
              </Card>
            </Col>
          </Row>

          {/* Revenue Chart */}
          <Card className="chart-card" style={{ marginBottom: 24 }}>
            <div className="chart-header">
              <Title level={4}>Revenue Trends</Title>
              <Text type="secondary">Monthly revenue and subscription growth</Text>
            </div>
            <Area {...revenueChartConfig} height={300} />
          </Card>

          {/* Company Growth Chart */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>Company Growth</Title>
                  <Text type="secondary">New vs total companies over time</Text>
                </div>
                <Column {...companyGrowthChartConfig} height={300} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>User Activity</Title>
                  <Text type="secondary">User engagement metrics</Text>
                </div>
                <Line {...userActivityChartConfig} height={300} />
              </Card>
            </Col>
          </Row>

          {/* Distribution Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={8}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>Plan Distribution</Title>
                  <Text type="secondary">Subscription plan breakdown</Text>
                </div>
                <Pie {...planDistributionConfig} height={300} />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>Company Types</Title>
                  <Text type="secondary">Micro vs Macro companies</Text>
                </div>
                <Pie {...companyTypeConfig} height={300} />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>Subscription Status</Title>
                  <Text type="secondary">Active, expired, and expiring</Text>
                </div>
                <Pie {...subscriptionStatusConfig} height={300} />
              </Card>
            </Col>
          </Row>

          {/* Additional Metrics Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>Monthly Performance</Title>
                  <Text type="secondary">Key metrics comparison</Text>
                </div>
                <div className="performance-metrics">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title="New Registrations"
                        value={2}
                        suffix="companies"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Active Subscriptions"
                        value={38}
                        suffix="companies"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="User Growth"
                        value={48}
                        suffix="users"
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Revenue Growth"
                        value={12.5}
                        suffix="%"
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="chart-card">
                <div className="chart-header">
                  <Title level={4}>System Health</Title>
                  <Text type="secondary">Overall system status and metrics</Text>
                </div>
                <div className="system-health">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div className="health-item">
                        <div className="health-label">Uptime</div>
                        <div className="health-value success">99.9%</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="health-item">
                        <div className="health-label">API Response</div>
                        <div className="health-value success">120ms</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="health-item">
                        <div className="health-label">Database</div>
                        <div className="health-value success">Healthy</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="health-item">
                        <div className="health-label">Storage</div>
                        <div className="health-value warning">75% used</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Recent Registrations Table */}
          <Card className="chart-card">
            <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Title level={4}>Recent Registrations</Title>
              </div>
              <Button
                type="link"
                onClick={() => navigate('/superadmin/companies')}
                style={{ padding: 0, fontWeight: 500, color: '#1890ff', marginTop: 4 }}
              >
                View All →
              </Button>
            </div>
            <Table
              dataSource={recentRegistrationsData}
              columns={recentRegistrationsColumns}
              pagination={false}
              rowKey="id"
              size="middle"
            />
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default SuperAdminAnalytics;
